import { getCities, getBlogs, getFAQs } from '@/lib/data'
import { GetStaticPaths, GetStaticProps } from 'next'
import FeaturedBlogs from '@/components/FeaturedBlogs'
import FAQ from '@/components/FAQ'
import Image from 'next/image'
import Head from 'next/head'
import Hero from '@/components/Hero'
import CityLinks from '@/components/CityLinks'

export default function CityPage({ city, blogs, faqs }: any) {
  if (!city) return <div className="container mx-auto p-8">Stadt nicht gefunden</div>
function generateCityMetaTags(city:any) {
    const keywordSets: Record<string, string[]> = {
        'Type1': ['Autoverwertung','Auto ohne Papier', 'Alte Auto', 'Motor schaden'],
        'Type2': ["Autoverwertung",'Stadt in Stadt', 'Autoentsorgung', 'Autoverschrottung'],
        'Type3': ["Autoverwertung",'Verwertungnachweis', 'Motorschaden', 'Unfallwagen'],
        'Type4': ['Ich möchte mein Auto verkaufen', 'Kostenlos Angebot', 'Kostenlos Abmeldung'],
        'Type5': ['Defektes Schrottauto', 'Schrott Platz', 'Kauft jedes Auto'],
        'Type6': ['Schrott Auto los werden', 'Motor schaden', 'Alte Auto'],
        'Type7': ['Autoentsorgung', 'Unfallwagen', 'Verwertungnachweis']
    };

    const cityIndex = city.index ?? 0;
    const totalTypes = 7;
    const selectedType = `Type${(cityIndex % totalTypes) + 1}`;
    const keywords = (keywordSets[selectedType] || []).join(', ');

const descriptionSets: Record<string, string> = {
  'Type1': `Kostenlose Abholung in ${city.name} ✓ Verwertung von Schrottautos ✓ Unfallwagen ✓ Autos mit Totalschaden ✓ Jetzt Termin sichern und umweltgerecht entsorgen!`,
  'Type2': `Kostenlose Abholung in ${city.name} ✓ Verwertung von Schrottautos ✓ Unfallwagen ✓ Autos mit Totalschaden ✓ Jetzt Termin sichern und umweltgerecht entsorgen!`,
  'Type3': `Kostenlose Abholung in ${city.name} ✓ Verwertung von Schrottautos ✓ Unfallwagen ✓ Autos mit Totalschaden ✓ Jetzt Termin sichern und umweltgerecht entsorgen!`,
  'Type4': `Kostenlose Abholung in ${city.name} ✓ Verwertung von Schrottautos ✓ Unfallwagen ✓ Autos mit Totalschaden ✓ Jetzt Termin sichern und umweltgerecht entsorgen!`,
  'Type5': `Kostenlose Abholung in ${city.name} ✓ Verwertung von Schrottautos ✓ Unfallwagen ✓ Autos mit Totalschaden ✓ Jetzt Termin sichern und umweltgerecht entsorgen!`,
  'Type6': `Kostenlose Abholung in ${city.name} ✓ Verwertung von Schrottautos ✓ Unfallwagen ✓ Autos mit Totalschaden ✓ Jetzt Termin sichern und umweltgerecht entsorgen!`,
  'Type7': `Kostenlose Abholung in ${city.name} ✓ Verwertung von Schrottautos ✓ Unfallwagen ✓ Autos mit Totalschaden ✓ Jetzt Termin sichern und umweltgerecht entsorgen!`
};

    const description = descriptionSets[selectedType] || city.description;

    return {
      title: `Autoverwertung ${city.name} | KFZ-Ankauf & Schrottauto`,
      description: `${description}`,
      keywords,
    };
}

// Within your component
const { title, description, keywords } = generateCityMetaTags(city);
  return (
    <>
<Head>
    <title>{title}</title>
    <meta name="description" content={description} />
    <meta name="keywords" content={keywords} />
    <meta property="og:title" content={title} />
    <meta property="og:description" content={description} />
    <meta property="og:image" content="/images/cars/reccar.webp" />
    <meta property="og:type" content="website" />
    <meta property="og:url" content={`https://autoverwertungs.de/${city.slug}`} />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content={city.image || '/logo.jpeg'} />
    <meta name="robots" content="index, follow" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0"></meta>
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        'name': `Autoverwertung ${city.name}`,
        'image': city.image || '/images/cars/reccar.webp',
        'telephone': process.env.NEXT_PUBLIC_PHONE_NUMBER || "01632337268",
        "email": "info@autoverwertungs.de",
        "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "325"
        },
        'address': {
            '@type': 'PostalAddress',
            'addressLocality': city.name,
            'addressCountry': 'DE'
        },
        'description': description,
        'url': `https://autoverwertungs.de/${city.slug}`
    }) }} />
</Head>
      <Hero city={city.name} slug={city.slug} />
      <div className="container mx-auto px-4 py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center mb-8">
          <div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Autoverwertung {city.name}</h2>
            <p className="text-base sm:text-lg text-gray-700 mt-4 leading-relaxed max-w-3xl">{city.description}</p>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Hier finden Sie Informationen, Services und Angebote für professionelle Autoverwertung {city.name}. Unser Team ist bereit, Ihnen bei Ihren Anfragen zu helfen.
            </p>
          </div>

          <div className="relative h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden shadow-lg">
            <Image
              src={city.image || '/images/cars/reccar.webp'}
              alt={`Autoverwertung und KFZ-Ankauf in ${city.name}`}
              fill
              quality={85}
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
              priority
            />
          </div>
        </div>
      </div>

      <FeaturedBlogs blogs={blogs} />

      <FAQ faqs={faqs} />
        {/* Internal linking: show 9 other cities */}
        <section className="bg-gray-50 py-8 md:py-12 lg:py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 md:mb-8">Weitere Standorte</h2>
            {(() => {
              const allCities = require('../../data/cities.json'); 
              const currentIndex = city.index ?? allCities.findIndex((c:any) => c.slug === city.slug);
              const otherCities = [];
              for (let i = 1; i <= 9; i++) {
                otherCities.push(allCities[(currentIndex + i) % allCities.length]);
              }
              return <CityLinks cities={otherCities} showAll={true} limit={9} />;
            })()}
          </div>
        </section>
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const cities = await getCities()
  const paths = cities.map((c: any) => ({ params: { city: c.slug } }))
  return { paths, fallback: false }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.city as string
  const cities = await getCities()
  const blogs = await getBlogs()
  const faqs = await getFAQs()
  const city = cities.find((c: any) => c.slug === slug) || null
  return { props: { city, blogs, faqs } }
}
